import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef
} from 'react';
import Paper from '@material-ui/core/Paper';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles, Button, Grid, TablePagination } from '@material-ui/core';
import FormHelperText from '@material-ui/core/FormHelperText';
import TableSortLabel from '@material-ui/core/TableSortLabel';

import PopupField from '../../../core/components/grid/PopupField';
import EmailDialog from './EmailDialog';
import { useNotify, TextInput, SaveButton } from 'react-admin';
import { saveAs } from 'file-saver';
import DistributionListApi from '../../../core/apis/DistributionListApi';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import { difference } from 'lodash';
import { splitAndTrimString } from '../../../core/utils/index';

const useStyles = makeStyles({
  tableWrapper: {
    maxHeight: '60vh',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px'
    },
    /* Track */
    '&::-webkit-scrollbar-track': {
      background: '#ebebeb',
      borderRadius: '10px',
      margin: '2px'
    },
    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: '#9E9E9E',
      borderRadius: '10px'
    },
    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#7b7a7a'
    }
  },
  checkboxContainer: {
    display: 'flex',
    minWidth: '175px',
    alignItems: 'center'
  },
  checkbox: {
    padding: '0'
  },
  button: {
    padding: '0px',
    margin: '0px'
  },
  actionBarContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  indicatorLabel: {
    fontSize: 14,
    color: '#757575',
    paddingRight: '18px'
  },
  excludedContactsLabel: {
    fontSize: 14,
    color: '#2962FF',
    paddingRight: '18px',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  indicatorNumber: {
    fontSize: 14,
    color: '#212121'
  },
  excludedContactsIndicatorLabel: {
    fontSize: 14,
    color: '#2962FF'
  },
  composeEmail: {
    marginLeft: '10px',
    border: 'solid 1px rgba(0, 0, 0, 0.12)',
    marginBottom: '9px',
    height: '35px'
  },
  indicatorDiv: {
    flex: 'none',
    marginBottom: '15px'
  },
  searchDiv: {
    display: 'flex',
    alignItems: 'flex-end'
  },
  searchTextInput: {
    marginRight: '20px'
  },
  buttonsDiv: {
    display: 'flex',
    alignSelf: 'flex-end'
  },
  helperSearchInput: {
    fontSize: '0.75rem',
    fontFamily: '"Segoe UI",Arial,sans-serif',
    color: 'rgba(0, 0, 0, 0.54)',
    fontWeight: '400',
    lineHeight: '1.66',
    marginBottom: '10px'
  },
  searchNotAddedInput: {
    marginTop: '25px',
    marginBottom: '10px'
  },
  paperContainer: {
    width: '100%'
  },
  table: {
    overflow: 'auto',
    maxHeight: 'calc(100vh - 364px)',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px'
    },
    /* Track */
    '&::-webkit-scrollbar-track': {
      background: '#ebebeb',
      borderRadius: '10px',
      margin: '2px'
    },
    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: '#9E9E9E',
      borderRadius: '10px'
    },
    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#7b7a7a'
    }
  },
  tableContainer: {
    borderTop: '0px'
  },
  checkboxCell: {
    width: '24px',
    borderLeft: '0',
    borderRight: '1px solid rgba(224, 224, 224, 1)'
  },
  cell: {
    borderLeft: '0',
    '&:last-child': {
      borderRight: '0'
    }
  },
  pagination: {
    width: '100%'
  }
});

const descendingComparator = (a, b, orderBy) => {
  switch (orderBy) {
    case 'contact':
      return (
        b['firstName']
          .toLowerCase()
          .localeCompare(a['firstName'].toLowerCase()) ||
        b['lastName'].toLowerCase().localeCompare(a['lastName'].toLowerCase())
      );
    case 'investor':
      return (b['investors'].length === 1
        ? b['investors'][0].name.toLowerCase()
        : 'Multiple Investors'
      ).localeCompare(
        a['investors'].length === 1
          ? a['investors'][0].name.toLowerCase()
          : 'Multiple Investors'
      );
    default:
      return b[orderBy].toLowerCase() < a[orderBy].toLowerCase() ? -1 : 1;
  }
};

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
};

const SummaryGrid = props => {
  const classes = useStyles();
  const [showEmaildDialog, setShowEmaildDialog] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const {
    choices,
    formprops,
    distributionList,
    dlOptions,
    hasComposeEmailDLPermission,
    hasExportPermission,
    userProfile,
    filterByName,
    filterByEmail,
    filterByFund,
    filterByInvestor,
    addExcludedContacts,
    finalData
  } = props;
  const { values: { recipients = [] } = {} } = formprops;
  const notify = useNotify();
  const [excludedContacts, setExcludedContacts] = useState([]);
  const [excludedContactsDialogOpen, setExcludedContactsDialogOpen] = useState(
    false
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [filteredExcludedContacts, setFilteredExcludedContacts] = useState([]);
  const [excludedContactsLoaded, setExcludedContactsLoaded] = useState(false);
  const [addDisabled, setAddDisabled] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('email');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = React.useState(0);

  const tableEl = useRef(null);
  const pageOptions = [25, 50, 100];
  const backIconProps = { 'aria-label': 'previous page' };
  const nextIconProps = { 'aria-label': 'next page' };

  /**
   * Click on button 'Download as Excel' to download the excel file
   */
  const handleClickDownloadAsExcel = useCallback(
    payload => {
      setLoading(true);
      DistributionListApi.getEmailAddressesExcelByFilter(payload)
        .then(resp => {
          const fileName = 'distributionlist.xlsx';
          saveAs(resp, fileName);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          notify('ra.page.error', { type: 'error' });
        });
    },
    [notify]
  );

  const handleChangePage = useCallback(
    (_event, newPage) => {
      tableEl.current.scrollTop = 0;
      setPage(newPage);
    },
    [tableEl]
  );

  const handleChangeRowsPerPage = useCallback(event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  }, []);

  const [paginationCls] = useMemo(() => [{ root: classes.pagination }], [
    classes
  ]);

  const filterExcludedContacts = event => {
    let excludedContactsFilter = splitAndTrimString(event.target.value, ',');

    let filteredList = excludedContacts.filter(item => {
      return (
        !excludedContactsFilter ||
        excludedContactsFilter.some(
          value =>
            item.firstName
              .concat(' ' + item.lastName)
              .toLowerCase()
              .includes(value === '' ? null : value.toLowerCase()) ||
            item.email
              .toLowerCase()
              .includes(value === '' ? null : value.toLowerCase()) ||
            item.fundName
              .toLowerCase()
              .includes(value === '' ? null : value.toLowerCase()) ||
            item.fundId
              .toLowerCase()
              .includes(value === '' ? null : value.toLowerCase())
        )
      );
    });

    setFilteredExcludedContacts(filteredList);
  };

  useEffect(() => {
    if (!excludedContactsLoaded && finalData && dlOptions) {
      const paramNotSelected = {
        fundIds: dlOptions.map(item => item.fundId),
        excludedUserFundRelations: finalData.map(item => {
          return {
            userId: item.userId,
            fundId: item.fundId
          };
        })
      };
      async function getExcludedContacts() {
        let responseList = await DistributionListApi.getExcludedContactsFromDistributionList(
          paramNotSelected
        );
        setExcludedContacts(responseList);
        setFilteredExcludedContacts(responseList);
      }

      // Execute the created function directly
      getExcludedContacts();
      setExcludedContactsLoaded(true);
    }
  }, [excludedContactsLoaded, dlOptions, finalData]);

  /**
   * Get contacts indicators (Memoized)
   */
  const contactsIndicators = useMemo(() => {
    const total = [];
    const distinct = [];
    const nonAdmin = [];
    choices.forEach(choice => {
      total.push(choice.email);
      choice.ccEmails.forEach(ccEmail => {
        total.push(ccEmail);
      });

      if (!distinct.includes(choice.email)) {
        distinct.push(choice.email);
        if (choice.userType === 'REGULAR_CONTACT') {
          nonAdmin.push(choice.email);
        }
      }
    });
    return {
      total: total.length,
      distinct: distinct.length,
      nonAdmin: nonAdmin.length
    };
  }, [choices]);

  /**
   * Get the list of recipients with ccEmails set to true (Memorized)
   */
  const recipientsWithCcEmails = useMemo(
    () =>
      recipients.filter(recipient => {
        return recipient.includeAllCcEmails;
      }),
    [recipients]
  );

  /**
   * Get the list of choices that have at least one ccEmail (Memorized)
   */
  const choicesHaveCcEmails = useMemo(
    () =>
      choices.filter(choice => {
        return choice.ccEmails.length > 0;
      }),
    [choices]
  );

  /**
   * Handle checkbox select primary email
   * @param {*} choice
   */
  const handleSelect = choice => {
    const array = [...recipients];
    const index = array.findIndex(
      obj => obj.userId === choice.userId && obj.fund === choice.fundId
    );

    if (index >= 0) {
      array.splice(index, 1);
    } else {
      array.push({
        userId: choice.userId,
        fund: choice.fundId,
        email: choice.email,
        ccEmails: choice.ccEmails,
        includeAllCcEmails: false
      });
    }

    formprops.form.change('recipients', array);
  };

  /**
   * Handle checkbox select ALL primary emails
   */
  const handleSelectAll = () => {
    let array = [...recipients];
    if (recipients.length < choices.length) {
      choices.forEach(choice => {
        const index = array.findIndex(
          obj => obj.userId === choice.userId && obj.fund === choice.fundId
        );

        if (index < 0) {
          array.push({
            userId: choice.userId,
            fund: choice.fundId,
            email: choice.email,
            ccEmails: choice.ccEmails,
            includeAllCcEmails: false
          });
        }
      });
    } else {
      array = [];
    }

    formprops.form.change('recipients', array);
  };

  /**
   * Handle checkbox select with CC email
   * @param {*} choice
   */
  const handleSelectWithCcEmails = choice => {
    const array = [...recipients];
    const index = array.findIndex(
      obj => obj.userId === choice.userId && obj.fund === choice.fundId
    );

    if (index >= 0) {
      array[index] = {
        ...array[index],
        includeAllCcEmails: !array[index].includeAllCcEmails
      };
    } else {
      array.push({
        userId: choice.userId,
        fund: choice.fundId,
        email: choice.email,
        ccEmails: choice.ccEmails,
        includeAllCcEmails: true
      });
    }

    formprops.form.change('recipients', array);
  };

  /**
   * Handle checkbox select ALL with CC emails
   */
  const handleSelectAllWithCcEmails = () => {
    let array = [];
    if (recipientsWithCcEmails.length < choicesHaveCcEmails.length) {
      choices.forEach(choice => {
        if (choice.ccEmails.length > 0) {
          array.push({
            userId: choice.userId,
            fund: choice.fundId,
            email: choice.email,
            ccEmails: choice.ccEmails,
            includeAllCcEmails: true
          });
        } else {
          const index = recipients.findIndex(
            obj => obj.userId === choice.userId && obj.fund === choice.fundId
          );
          if (index >= 0) {
            array.push({
              userId: choice.userId,
              fund: choice.fundId,
              email: choice.email,
              ccEmails: choice.ccEmails
            });
          }
        }
      });
    } else {
      array = recipients.map(recipient => {
        return { ...recipient, includeAllCcEmails: false };
      });
    }

    formprops.form.change('recipients', array);
  };

  /**
   * When we click on the checkbox in a row
   * @param {*} checked
   * @param {*} item
   */
  const handleRowSelect = useCallback((checked, item) => {
    if (checked) {
      setSelectedRows(selectedRows => selectedRows.concat(item));
    } else {
      setSelectedRows(selectedRows =>
        selectedRows.filter(itemRow => item !== itemRow)
      );
    }
  }, []);

  /**
   * Calculating number of rows depending of the filter
   */
  const numSelectedRows = useMemo(() => {
    let newArray = [];
    filteredExcludedContacts.forEach(item => {
      if (selectedRows.includes(item)) {
        newArray.push(item);
      }
    });
    return newArray.length;
  }, [filteredExcludedContacts, selectedRows]);

  const rowCount = useMemo(() => {
    return filteredExcludedContacts.length;
  }, [filteredExcludedContacts]);

  /**
   * When we click the global checkbox
   */
  const handleClickAll = useCallback(() => {
    if (numSelectedRows === rowCount) {
      let newArray = [];
      filteredExcludedContacts.forEach(item => {
        if (selectedRows.includes(item)) {
          newArray.push(item);
        }
      });
      setSelectedRows(difference(selectedRows, newArray));
    } else {
      setSelectedRows(selectedRows.concat(filteredExcludedContacts));
    }
  }, [numSelectedRows, filteredExcludedContacts, rowCount, selectedRows]);

  const handleEmailDialogToggle = toggle => {
    setShowEmaildDialog(toggle);
  };

  const handleRequestSort = (_event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = property => event => {
    handleRequestSort(event, property);
  };

  useEffect(() => {
    setAddDisabled(selectedRows.length === 0);
  }, [selectedRows]);

  return (
    <>
      <Grid
        item
        wrap="nowrap"
        container
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <div className={classes.searchDiv}>
          <TextInput
            source="filterByEmail"
            label="Search by Email"
            variant="standard"
            helperText={false}
            onChange={event => filterByEmail(event)}
            className={classes.searchTextInput}
          />

          <TextInput
            source="filterByContact"
            label="Search by Contact"
            variant="standard"
            helperText={false}
            onChange={event => filterByName(event)}
            className={classes.searchTextInput}
          />
          <TextInput
            source="filterByFund"
            label="Search by Fund"
            variant="standard"
            helperText={false}
            onChange={event => filterByFund(event)}
            className={classes.searchTextInput}
          />
          <TextInput
            source="filterByInvestor"
            label="Search by Investor"
            variant="standard"
            helperText={false}
            onChange={event => filterByInvestor(event)}
            className={classes.searchTextInput}
          />
          <div className={classes.indicatorDiv}>
            <span className={classes.indicatorLabel}>
              Total Number of Contacts:
              <span
                className={classes.indicatorNumber}
              >{` ${contactsIndicators.total}`}</span>
            </span>
            <span className={classes.indicatorLabel}>
              Distinct Contacts:
              <span
                className={classes.indicatorNumber}
              >{` ${contactsIndicators.distinct}`}</span>
            </span>
            <span className={classes.indicatorLabel}>
              Non-Admin Contacts:
              <span
                className={classes.indicatorNumber}
              >{` ${contactsIndicators.nonAdmin}`}</span>
            </span>
            <span
              className={classes.excludedContactsLabel}
              onClick={() => setExcludedContactsDialogOpen(true)}
            >
              Excluded Contacts:
              <span
                className={classes.excludedContactsIndicatorLabel}
              >{` ${excludedContacts.length}`}</span>
            </span>
          </div>
        </div>
        <div>
          {hasExportPermission && (
            <Button
              color="primary"
              variant="outlined"
              className={classes.composeEmail}
              onClick={
                isLoading ? null : () => handleClickDownloadAsExcel(dlOptions)
              }
              disabled={recipients.length < 1 || isLoading}
            >
              Download as Excel
            </Button>
          )}
          {hasComposeEmailDLPermission && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleEmailDialogToggle(true)}
              className={classes.composeEmail}
            >
              Compose Email
            </Button>
          )}
        </div>
      </Grid>
      <div className={classes.helperSearchInput}>
        You can input multiple keywords by separating them with comma
      </div>
      <div className={classes.tableWrapper} ref={tableEl}>
        <Paper className={classes.paper}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={createSortHandler('email')}
                  >
                    <div className={classes.checkboxContainer}>
                      <Checkbox
                        className={classes.checkbox}
                        indeterminate={
                          recipients.length > 0 &&
                          recipients.length < choices.length
                        }
                        checked={
                          recipients.length > 0 &&
                          recipients.length === choices.length
                        }
                        onChange={handleSelectAll}
                        onClick={e => {
                          e.stopPropagation();
                        }}
                      />
                      Email
                      {orderBy === 'email' ? (
                        <span className={classes.visuallyHidden}></span>
                      ) : null}
                    </div>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <div className={classes.checkboxContainer}>
                    <Checkbox
                      className={classes.checkbox}
                      indeterminate={
                        recipientsWithCcEmails.length > 0 &&
                        recipientsWithCcEmails.length <
                          choicesHaveCcEmails.length
                      }
                      checked={
                        recipientsWithCcEmails.length > 0 &&
                        recipientsWithCcEmails.length ===
                          choicesHaveCcEmails.length
                      }
                      onChange={handleSelectAllWithCcEmails}
                    />
                    CC Email
                  </div>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'contact'}
                    direction={orderBy === 'contact' ? order : 'asc'}
                    onClick={createSortHandler('contact')}
                  >
                    Contact
                    {orderBy === 'contact' ? (
                      <span className={classes.visuallyHidden}></span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'fundName'}
                    direction={orderBy === 'fundName' ? order : 'asc'}
                    onClick={createSortHandler('fundName')}
                  >
                    Fund
                    {orderBy === 'fundName' ? (
                      <span className={classes.visuallyHidden}></span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'investor'}
                    direction={orderBy === 'investor' ? order : 'asc'}
                    onClick={createSortHandler('investor')}
                  >
                    Investor
                    {orderBy === 'investor' ? (
                      <span className={classes.visuallyHidden}></span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Permission Group</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(choices, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(choice => {
                  return (
                    <TableRow key={choice.userId + choice.fundId}>
                      <TableCell>
                        <div className={classes.checkboxContainer}>
                          <Checkbox
                            className={classes.checkbox}
                            checked={
                              recipients.findIndex(
                                obj =>
                                  obj.userId === choice.userId &&
                                  obj.fund === choice.fundId
                              ) >= 0
                            }
                            onClick={event => handleSelect(choice)}
                          />
                          {choice.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={classes.checkboxContainer}>
                          <Checkbox
                            className={classes.checkbox}
                            checked={
                              recipientsWithCcEmails.findIndex(
                                obj =>
                                  obj.userId === choice.userId &&
                                  obj.fund === choice.fundId
                              ) >= 0
                            }
                            onClick={event => handleSelectWithCcEmails(choice)}
                            disabled={choice.ccEmails.length < 1}
                          />
                          {choice.ccEmails.length === 1 && choice.ccEmails[0]}
                          {choice.ccEmails.length > 1 && (
                            <PopupField
                              data={choice.ccEmails}
                              label="Multiple CC Emails"
                              headLabel="CC Email"
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {choice.firstName} {choice.lastName}
                      </TableCell>
                      <TableCell>{choice.fundName}</TableCell>
                      <TableCell>
                        {choice.investors.length === 1 &&
                          choice.investors[0].name}
                        {choice.investors.length > 1 && (
                          <PopupField
                            data={choice.investors}
                            label="Multiple Investors"
                            optionText="name"
                            headLabel="Investor Name"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {choice.groups.length > 0 && (
                          <PopupField
                            data={choice.groups}
                            label={choice.groups.length}
                            optionText="name"
                            headLabel="Permission Group Name"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Paper>
      </div>
      <TablePagination
        classes={paginationCls}
        rowsPerPageOptions={pageOptions}
        component="div"
        count={choices.length}
        rowsPerPage={rowsPerPage}
        page={page}
        backIconButtonProps={backIconProps}
        nextIconButtonProps={nextIconProps}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {recipients.length < 1 && (
        <FormHelperText error>
          At least one recipient must be selected.
        </FormHelperText>
      )}
      <EmailDialog
        showDialog={showEmaildDialog}
        handleClose={() => handleEmailDialogToggle(false)}
        choices={choices}
        recipients={recipients}
        templateId={distributionList.templateId}
        userProfile={userProfile}
        dlOptions={dlOptions}
      />
      <Dialog
        fullWidth
        open={excludedContactsDialogOpen}
        aria-label={`Add Excluded Contacts`}
        maxWidth={'lg'}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return null;
          setExcludedContactsDialogOpen(false);
        }}
      >
        <DialogTitleWithCloseButton
          onClose={() => setExcludedContactsDialogOpen(false)}
        >
          {'Add Excluded Contacts'}
        </DialogTitleWithCloseButton>
        <DialogContent>
          <span className={classes.indicatorLabel}>
            These Contacts are excluded from Distribution List.
            <br />
            You can add them back, by selecting Contacts you want, and clicking
            the Add button.
          </span>
          <TextInput
            source="filterExcludedContacts"
            variant="standard"
            placeholder="Search"
            helperText={false}
            onChange={event => filterExcludedContacts(event)}
            className={classes.searchNotAddedInput}
          />
          <Paper className={classes.paperContainer}>
            <div className={classes.table}>
              <Table
                stickyHeader
                aria-label="sticky table"
                size="small"
                className={classes.tableContainer}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      className={classes.checkboxCell}
                    >
                      <Checkbox
                        indeterminate={
                          numSelectedRows > 0 && numSelectedRows < rowCount
                        }
                        checked={numSelectedRows === rowCount && rowCount > 0}
                        onClick={handleClickAll}
                        className={classes.checkbox}
                      />
                    </TableCell>
                    {[
                      { id: 'Contact Name' },
                      { id: 'Email' },
                      { id: 'Fund Name' },
                      { id: 'Fund ID' }
                    ].map(headCell => (
                      <TableCell className={classes.cell} key={headCell.id}>
                        {headCell.id}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredExcludedContacts.map((item, index) => {
                    return (
                      <TableRow key={index} hover>
                        <TableCell
                          padding="checkbox"
                          className={classes.checkboxCell}
                        >
                          <Checkbox
                            onClick={event =>
                              handleRowSelect(event.target.checked, item)
                            }
                            className={item.checkbox}
                            checked={selectedRows.includes(item)}
                          />
                        </TableCell>
                        <TableCell>
                          {item.firstName + ' ' + item.lastName}
                        </TableCell>
                        <TableCell>{item.email}</TableCell>
                        <TableCell>{item.fundName}</TableCell>
                        <TableCell>{item.fundId}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Paper>
        </DialogContent>
        <DialogActions>
          <SaveButton
            handleSubmitWithRedirect={() => {
              addExcludedContacts(selectedRows);
              setSelectedRows([]);
              let newExcludedContacts = [...excludedContacts];
              newExcludedContacts = newExcludedContacts.filter(
                item => !selectedRows.includes(item)
              );
              setExcludedContacts(newExcludedContacts);
              setFilteredExcludedContacts(newExcludedContacts);
              setExcludedContactsDialogOpen(false);
            }}
            disabled={addDisabled}
            label={'Add'}
            icon={<></>}
          />
          <Button
            onClick={() => setExcludedContactsDialogOpen(false)}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SummaryGrid;
